import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChartApi } from '@/components';
import { HeatMapChartBucket, HeatMapChartData, TrimBoundMode } from '../heat-map-model';

/** Floor value for log-scale math: small enough to be invisible on any realistic axis,
 *  large enough to avoid floating-point underflow in log10(). */
export const LOG_EPSILON = 1e-10;

const Y_AXIS_DATA_ZOOM_ID = 'y-inside';

interface TrimRange {
  min_mode?: TrimBoundMode;
  max_mode?: TrimBoundMode;
  min?: number;
  max?: number;
}

interface BucketCount {
  lower: number;
  upper: number;
  count: number;
}

export interface YAxisBoundsResult {
  effectiveBounds: { min: number; max: number };
  trimmedBounds: { min: number; max: number };
  yScaleFactor: number;
}

function getRawBucketBounds(buckets: HeatMapChartBucket[]): { minBound: number; maxBound: number } {
  const lower = buckets[0]?.lower ?? 0;
  const minBound = Number.isFinite(lower) ? lower : 0;
  let maxBound = 0;
  for (let i = buckets.length - 1; i >= 0; i--) {
    const upper = buckets[i]?.upper;
    if (upper !== undefined && Number.isFinite(upper)) {
      maxBound = upper;
      break;
    }
  }
  return { minBound, maxBound: maxBound || 1 };
}

function computeEffectiveBounds({
  rawBounds,
  buckets,
  scaleType,
}: {
  rawBounds: { minBound: number; maxBound: number };
  buckets: HeatMapChartBucket[];
  scaleType: 'value' | 'log';
}): { min: number; max: number } {
  const { minBound, maxBound } = rawBounds;

  if (scaleType !== 'log') {
    const min = Math.max(0, minBound);
    const max = Math.max(maxBound, min + LOG_EPSILON);
    return { min, max };
  }

  const finiteBuckets = buckets.filter(
    (b) => Number.isFinite(b.lower) && Number.isFinite(b.upper) && b.upper > b.lower && b.upper > LOG_EPSILON
  );
  if (finiteBuckets.length === 0) {
    const fallbackMax = maxBound > LOG_EPSILON ? maxBound : 1;
    return { min: LOG_EPSILON, max: fallbackMax };
  }

  const lastBucket = finiteBuckets[finiteBuckets.length - 1]!;

  const hasInfBucket = buckets.some((b) => !Number.isFinite(b.upper));
  let extendedMax: number;
  if (hasInfBucket) {
    const lastUpper = Math.max(LOG_EPSILON, lastBucket.upper);
    const lastLower = Math.max(LOG_EPSILON, lastBucket.lower);
    const lastLogSpan = Math.max(LOG_EPSILON, Math.log10(lastUpper) - Math.log10(lastLower));
    extendedMax = Math.pow(10, Math.log10(lastUpper) + lastLogSpan);
  } else {
    extendedMax = Math.max(LOG_EPSILON, lastBucket.upper);
  }

  let extendedMin: number;
  const dataMin = finiteBuckets[0]!.lower;
  if (dataMin <= 0) {
    const refBucket = finiteBuckets.find((b) => b.lower > LOG_EPSILON) ?? finiteBuckets[0]!;
    const refUpper = Math.max(LOG_EPSILON, refBucket.upper);
    const refLower = Math.max(LOG_EPSILON, refBucket.lower);
    const refLogSpan = Math.max(LOG_EPSILON, Math.log10(refUpper) - Math.log10(refLower));
    const firstPositiveBound = Math.max(LOG_EPSILON, finiteBuckets[0]!.upper);
    extendedMin = Math.pow(10, Math.log10(firstPositiveBound) - refLogSpan);
  } else {
    extendedMin = dataMin;
  }

  const min = Math.max(LOG_EPSILON, extendedMin);
  const max = Math.max(min + LOG_EPSILON, extendedMax);
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    const fallbackMax = maxBound > LOG_EPSILON ? maxBound : 1;
    return { min: LOG_EPSILON, max: fallbackMax };
  }

  return { min, max };
}

interface BucketDistributionResult {
  entries: BucketCount[];
  maxPerBucket: number;
}

function buildBucketDistribution({
  timeSlices,
  buckets,
  bounds,
  scaleType,
}: {
  timeSlices: HeatMapChartData['timeSlices'];
  buckets: HeatMapChartBucket[];
  bounds: { min: number; max: number };
  scaleType: 'value' | 'log';
}): BucketDistributionResult {
  let maxPerBucket = 0;
  const bucketCounts = new Map<number, BucketCount>();
  for (const timeSlice of timeSlices) {
    for (const dp of timeSlice.dataPoints) {
      const valPerBucket = dp.value / dp.bucketSpan;
      maxPerBucket = Math.max(maxPerBucket, valPerBucket);
      for (let i = 0; i < dp.bucketSpan; i++) {
        const bucketIdx = dp.bucketIndex + i;
        const bucket = buckets[bucketIdx];
        if (!bucket) continue;
        let lower = Number.isFinite(bucket.lower) ? bucket.lower : bounds.min;
        const upper = Number.isFinite(bucket.upper) ? bucket.upper : bounds.max;
        if (scaleType === 'log' && lower <= 0) lower = bounds.min;
        if (upper <= lower) continue;
        const existing = bucketCounts.get(bucketIdx);
        if (existing) {
          existing.count += valPerBucket;
        } else {
          bucketCounts.set(bucketIdx, { lower, upper, count: valPerBucket });
        }
      }
    }
  }
  return {
    entries: Array.from(bucketCounts.values()).sort((a, b) => a.lower - b.lower),
    maxPerBucket,
  };
}

// pct is a 0–100 percentile rank over sample counts (inverse CDF), not a fraction of the Y-axis span.
function percentileToValue(pct: number, distribution: BucketCount[], fallback: number): number {
  if (distribution.length === 0) return fallback;
  const totalCount = distribution.reduce((sum, b) => sum + b.count, 0);
  if (totalCount === 0) return fallback;
  const target = (pct / 100) * totalCount;
  let cum = 0;
  for (const bucket of distribution) {
    if (cum + bucket.count >= target) {
      const frac = bucket.count > 0 ? (target - cum) / bucket.count : 0;
      return bucket.lower + frac * (bucket.upper - bucket.lower);
    }
    cum += bucket.count;
  }
  return distribution[distribution.length - 1]!.upper;
}

function resolveTrimRange({
  trimRange,
  effectiveBounds,
  distribution,
}: {
  trimRange: TrimRange | undefined;
  effectiveBounds: { min: number; max: number };
  distribution: BucketCount[];
}): { min: number; max: number } | null {
  if (!trimRange) return null;
  const minMode = trimRange.min_mode ?? 'Value';
  const maxMode = trimRange.max_mode ?? 'Value';

  const resolveBound = (mode: string, val: number | undefined, fallback: number): number => {
    if (mode === 'Value' && val !== undefined) return val;
    if (mode === 'Percentile' && val !== undefined) return percentileToValue(val, distribution, fallback);
    return fallback;
  };

  const min = resolveBound(minMode, trimRange.min, effectiveBounds.min);
  const max = resolveBound(maxMode, trimRange.max, effectiveBounds.max);
  if (min < max) return { min, max };
  return null;
}

/** ECharts log axes don't render sub-pixel values well when the minimum is far
 *  below 1 (e.g. 0.001). Multiplying all Y values by yScaleFactor shifts them
 *  into a range ECharts can plot accurately; axis labels divide it back out. */
function computeYScaleFactor(scaleType: 'value' | 'log', trimmedBounds: { min: number; max: number }): number {
  if (scaleType !== 'log') return 1;
  const minVal = Math.max(LOG_EPSILON, trimmedBounds.min);
  if (minVal >= 1) return 1;
  return Math.pow(10, Math.ceil(-Math.log10(minVal)));
}

/**
 * Derives Y-axis numeric bounds from histogram buckets, optional percentile/value trim, and linear vs log scale.
 */
export function useYAxisBounds({
  buckets,
  timeSlices,
  scaleType,
  trimRange,
}: {
  buckets: HeatMapChartBucket[];
  timeSlices: HeatMapChartData['timeSlices'];
  scaleType: 'value' | 'log';
  trimRange: TrimRange | undefined;
}): YAxisBoundsResult {
  const rawBounds = useMemo(() => getRawBucketBounds(buckets), [buckets]);
  const effectiveBounds = useMemo(
    () => computeEffectiveBounds({ rawBounds, buckets, scaleType }),
    [rawBounds, buckets, scaleType]
  );
  const distribution = useMemo(
    () => buildBucketDistribution({ timeSlices, buckets, bounds: effectiveBounds, scaleType }).entries,
    [timeSlices, buckets, effectiveBounds, scaleType]
  );
  const resolved = useMemo(
    () => resolveTrimRange({ trimRange, effectiveBounds, distribution }),
    [trimRange, effectiveBounds, distribution]
  );
  const trimmedBounds = useMemo(() => {
    if (!resolved || resolved.min >= resolved.max) return effectiveBounds;
    return resolved;
  }, [effectiveBounds, resolved]);
  const yScaleFactor = useMemo(() => computeYScaleFactor(scaleType, trimmedBounds), [scaleType, trimmedBounds]);

  return { effectiveBounds, trimmedBounds, yScaleFactor };
}

export interface YAxisZoomResult {
  visibleYRange: { min: number; max: number } | null;
  dispatchYZoom: (min: number, max: number) => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleResetYZoom: () => void;
  readAndUpdateVisibleYRange: () => void;
  yAxisDistributionEntries: Array<{ lower: number; upper: number; count: number }>;
  trimmedMaxPerBucket: number;
  /** Ref kept in sync with yScaleFactor — needed by renderItem and readAndUpdateVisibleYRange. */
  yScaleFactorRef: MutableRefObject<number>;
}

/**
 * Y-axis dataZoom dispatch, minimap visible range, and per-bucket counts within trimmed bounds.
 */
export function useYAxisZoom({
  chartApiRef,
  gridRectRef,
  data,
  trimmedBounds,
  yScaleFactor,
  scaleType,
}: {
  chartApiRef: MutableRefObject<ChartApi | undefined>;
  gridRectRef: MutableRefObject<{ x: number; y: number; width: number; height: number } | undefined>;
  data: HeatMapChartData;
  trimmedBounds: { min: number; max: number };
  yScaleFactor: number;
  scaleType: 'value' | 'log';
}): YAxisZoomResult {
  const yScaleFactorRef = useRef(yScaleFactor);
  yScaleFactorRef.current = yScaleFactor;

  // Reset Y dataZoom when trim bounds change (skip first render).
  // Depend on primitive values so a new object reference with identical
  // min/max doesn't spuriously reset the user's zoom.
  const trimBoundsKey = `${trimmedBounds.min}:${trimmedBounds.max}`;
  const trimRangeMounted = useRef(false);
  useEffect(() => {
    if (!trimRangeMounted.current) {
      trimRangeMounted.current = true;
      return;
    }
    chartApiRef.current?.dispatchAction({
      type: 'dataZoom',
      dataZoomId: Y_AXIS_DATA_ZOOM_ID,
      start: 0,
      end: 100,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimBoundsKey]);

  const [visibleYRange, setVisibleYRange] = useState<{ min: number; max: number } | null>(null);

  const dispatchYZoom = useCallback(
    (min: number, max: number) => {
      chartApiRef.current?.dispatchAction({
        type: 'dataZoom',
        dataZoomId: Y_AXIS_DATA_ZOOM_ID,
        startValue: min * yScaleFactor,
        endValue: max * yScaleFactor,
      });
    },
    [yScaleFactor, chartApiRef]
  );

  const zoomY = useCallback(
    (factor: number) => {
      const gr = gridRectRef.current;
      if (!chartApiRef.current || !gr) return;
      const topVal = chartApiRef.current.convertFromPixel({ gridIndex: 0 }, [0, gr.y]);
      const botVal = chartApiRef.current.convertFromPixel({ gridIndex: 0 }, [0, gr.y + gr.height]);
      if (!topVal || !botVal) return;
      const currentMin = botVal[1]! / yScaleFactor;
      const currentMax = topVal[1]! / yScaleFactor;

      const isLog = scaleType === 'log';
      const toVisual = (v: number) => (isLog ? Math.log10(Math.max(v, LOG_EPSILON)) : v);
      const fromVisual = (v: number) => (isLog ? Math.pow(10, v) : v);

      const vMin = toVisual(currentMin);
      const vMax = toVisual(currentMax);
      const vCenter = (vMin + vMax) / 2;
      const newHalf = ((vMax - vMin) / 2) * factor;

      dispatchYZoom(
        Math.max(trimmedBounds.min, fromVisual(vCenter - newHalf)),
        Math.min(trimmedBounds.max, fromVisual(vCenter + newHalf))
      );
    },
    [scaleType, trimmedBounds, dispatchYZoom, yScaleFactor, chartApiRef, gridRectRef]
  );

  const handleZoomIn = useCallback(() => zoomY(0.6), [zoomY]);
  const handleZoomOut = useCallback(() => zoomY(1 / 0.6), [zoomY]);
  const handleResetYZoom = useCallback(
    () => dispatchYZoom(trimmedBounds.min, trimmedBounds.max),
    [dispatchYZoom, trimmedBounds]
  );

  const readAndUpdateVisibleYRange = useCallback(() => {
    const gr = gridRectRef.current;
    if (!chartApiRef.current || !gr) return;
    const topVal = chartApiRef.current.convertFromPixel({ gridIndex: 0 }, [0, gr.y]);
    const botVal = chartApiRef.current.convertFromPixel({ gridIndex: 0 }, [0, gr.y + gr.height]);
    if (topVal && botVal) {
      const newMin = botVal[1]! / yScaleFactorRef.current;
      const newMax = topVal[1]! / yScaleFactorRef.current;
      setVisibleYRange((prev) => {
        if (prev && Math.abs(prev.min - newMin) < 1e-12 && Math.abs(prev.max - newMax) < 1e-12) {
          return prev;
        }
        return { min: newMin, max: newMax };
      });
    }
  }, [chartApiRef, gridRectRef]);

  const { yAxisDistributionEntries, trimmedMaxPerBucket } = useMemo(() => {
    const { entries, maxPerBucket } = buildBucketDistribution({
      timeSlices: data.timeSlices,
      buckets: data.buckets,
      bounds: trimmedBounds,
      scaleType,
    });
    return { yAxisDistributionEntries: entries, trimmedMaxPerBucket: maxPerBucket };
  }, [data, trimmedBounds, scaleType]);

  return {
    visibleYRange,
    dispatchYZoom,
    handleZoomIn,
    handleZoomOut,
    handleResetYZoom,
    readAndUpdateVisibleYRange,
    yAxisDistributionEntries,
    trimmedMaxPerBucket,
    yScaleFactorRef,
  };
}
